/**
 * @Description 热点类，包括各种热点类以及热点管理类
 * @Author Kida
 * @Date 2020-10-7
 * @Version 1.0
 * @todo 1、弹窗类完善；2、加入跳转类
 */
import * as THREE from 'three'
import * as CanvasUtil from './CanvasUtil'
import { CameraController } from './CameraController'
import { VerticalVedioPanel, ExtraObjectSet } from './ExtraObject'
import { ViewSet } from './View'
import * as Util from './Utils'
import { config } from './config'
import { AudioManager } from './AudioManager'
import { detectIOS } from './Device/Browser'
import { Events } from './Events'
import { EventEmitter } from 'events/events'
/**
 * 热点类，基类
 */
export class Hotpoint {
  /**
   * 热点初始化函数
   * @param {string} id 热点id
   * @param {string} title 热点标题
   * @param {THREE.Vector3} pos 热点坐标
   * @param {string} hpType 热点类型
   * @param {number} cpointId 热点对应相机点位id
   * @param {boolean} interactable 是否可以交互
   */
  constructor(id, title, pos, hpType, cpointId, interactable = true) {
    /**
     * @type {string} 热点id
     */
    this.id = id
    /**
     * @type {string} 热点标题
     */
    this.title = title
    /**
     * @type {string} 热点类型
     */
    this.hpType = hpType
    /**
     * @type {number} 热点对应相机点位id
     */
    this.cpointId = cpointId
    /**
     * @type {boolean} 热点是否可以交互
     */
    this.interactable = interactable
    /**
     * @type {THREE.Group} 热点的图标集合
     */
    this.group = new THREE.Group()
    /**
     * @type {number} 热点播放时间
     */
    this.palyTime = config.Hotpoint.AnimationWatiTime
    /**
     * @type {THREE.Vector3} 热点坐标
     */
    this.position = pos
    /**
     * @type {Number} 热点缩放比例
     */
    this.scaleRatio = 1
  }

  get position() {
    return this.group.position
  }

  set position(value) {
    this.group.position.copy(value)
  }

  /**
   * 获取是否可视
   */
  get visible() {
    return this.group.visible
  }

  /**
   * 设置热点是否可视
   */
  set visible(value) {
    this.group.visible = value
  }

  resize(ratio) {
    this.scaleRatio = ratio
  }

  async getPlayTime() {
    return this.palyTime
  }
}

/**
 * 可交互热点
 */
class InteractableHotpoint extends Hotpoint {
  /**
   * 可交互热点初始化函数
   * @param {string} id 热点id
   * @param {string} title 热点标题
   * @param {THREE.Vector3} pos 热点坐标
   * @param {string} hpType 热点类型
   * @param {number} cpointId 热点对应相机点位id
   * @param {string} tagUrl 热点图标的url
   * @param {Object} style 样式
   */
  constructor(id, title, pos, hpType, cpointId, tagUrl, style) {
    super(id, title, pos, hpType, cpointId)
    /**
     * @type {string} 热点图标url
     */
    this.tagUrl = tagUrl

    /**
     * @type {boolean} 热点是否根据摄像机缩放
     */
    this.__scaleable = true

    this.defaultTextScale = new THREE.Vector3()
    this.showText = style.isShowTitle || false

    this.createObj(pos, tagUrl, title, style, !!style.switchAttenuation)

    // this.scaleable = !!style.switchAttenuation
  }

  /**
   * 热点是否根据摄像机缩放
   */
  get scaleable() {
    return this.__scaleable
  }

  /**
   * 热点是否根据摄像机缩放
   */
  set scaleable(value) {
    if (this.__scaleable === value) {
      return
    }

    if (this.showText) {
      let sizeA = false
      const scale = new THREE.Vector3()

      if (value) {
        sizeA = true
        scale.copy(config.Hotpoint.SizeScale)
      } else {
        sizeA = false
        scale.copy(config.Hotpoint.NoSizeScale)
      }
      scale.multiplyScalar(this.scaleRatio)

      this.group.children[1].material.sizeAttenuation = sizeA
      this.group.children[1].scale.copy(this.defaultTextScale).multiply(scale)
    }

    this.__scaleable = value
  }

  resize(ratio) {
    super.resize(ratio)
    if (!this.scaleable && this.showText) {
      this.group.children[1].scale.copy(this.defaultTextScale).multiply(config.Hotpoint.NoSizeScale).multiplyScalar(ratio)
    }
  }

  /**
   * 创建Obj
   *
   * @static
   * @param {THREE.Vector3} pos 位置
   * @param {String} tagUrl 图片icon
   * @param {String} title 标题
   * @param {Object} style 样式元素
   * @returns {THREE.Sprite[]} 创建的元素
   * @memberof InteractableHotpoint
   */
  async createObj(pos, tagUrl, title, style, sa) {
    // 创建icon
    const material = new THREE.SpriteMaterial({ transparent: true, alphaTest: 0.1 })
    const icon = new THREE.Sprite(material)
    icon.scale.multiply(config.Hotpoint.TextureScale)
    console.log('111----->', config.Hotpoint.TextureScale, config);

    icon.hpId = this.id
    icon.name = 'i'

    this.group.add(icon)

    // 加载失败时处理
    try {
      const tex = await (new THREE.TextureLoader().loadAsync(tagUrl))
      tex.wrapS = THREE.RepeatWrapping
      tex.wrapT = THREE.RepeatWrapping

      this.group.children[0].material.map = tex
      this.group.children[0].material.needsUpdate = true
      this.processMap(tex)
    } catch (e) {
    }

    // 创建字体
    if (style.isShowTitle) {
      const canvas = document.createElement('canvas')
      CanvasUtil.drawRoundText(canvas, title, style.fontColor, style.backgroundColor)

      const canvasTexture = new THREE.Texture(canvas)
      canvasTexture.wrapS = THREE.ClampToEdgeWrapping
      canvasTexture.wrapT = THREE.ClampToEdgeWrapping
      canvasTexture.needsUpdate = true

      const textObj = this.newTitleObj(style, canvas)
      this.defaultTextScale.copy(textObj.scale)
      textObj.scale.multiply(config.Hotpoint.SizeScale)
      textObj.material.map = canvasTexture

      this.group.add(textObj)
    }
    this.scaleable = !!sa
  }

  /**
   * 处理贴图，查看是否需要更新
   * @param {THREE.Texture} tex 贴图
   */
  processMap(tex) {
    this.map = tex
    this.tilesHorizontal = 1
    this.tilesVertical = 1
    this.tileNum = 1

    const w = this.map.image.width
    const h = this.map.image.height

    // 如果宽高不相等，则进行动画处理
    // console.log(w, h)
    if (w !== h) {
      if (w > h && w / h > 2) {
        this.tilesVertical = 1
        this.tilesHorizontal = Math.ceil(w / h)
      } else if (h > w && h / w > 2) {
        this.tilesVertical = Math.ceil(h / w)
        this.tilesHorizontal = 1
      }

      this.tileNum = this.tilesVertical * this.tilesHorizontal
      this.map.repeat = new THREE.Vector2(1 / this.tilesHorizontal, 1 / this.tilesVertical)
      this.passFrame = 0
      this.intervalFrame = config.Hotpoint.InconAnimationFrame
      this.currTile = 0

      // 动画
      this.update = function () {
        this.passFrame++
        if (this.passFrame !== this.intervalFrame) return
        this.passFrame = 0
        this.currTile = (this.currTile + 1) % this.tileNum
        this.map.offset.x = this.currTile % this.tilesHorizontal / this.tilesHorizontal
        this.map.offset.y = Math.floor(this.currTile / this.tilesHorizontal) / this.tilesVertical
      }
    } else {
      delete this.update
    }
  }

  /**
   * 创建标题对象
   * @param {Objcet} style 样式
   * @param {HTMLCanvasElement} canvas canvas元素
   */
  newTitleObj(style, canvas) {
    const texMat = new THREE.SpriteMaterial({ transparent: true, alphaTest: 0.05 })

    const textObj = new THREE.Sprite(texMat)
    textObj.position.set(0, -150, 0)

    textObj.scale.set(canvas.width / canvas.height, 1, 1).multiplyScalar(
      style.fontSize)

    textObj.name = 't'
    textObj.hpId = this.id
    return textObj
  }
}

/**
 * 标题热点
 */
export class TitleHotpoint extends Hotpoint {
  /**
   * 标题便签初始化函数
   * @param {string} id 热点id
   * @param {string} title 热点标题
   * @param {THREE.Vector3} pos 热点坐标
   * @param {number} cpointId 热点对应相机点位id
   */
  constructor(id, title, pos, cpointId) {
    super(id, title, pos, 'Title', cpointId, false)

    const size = { x: 0, y: 0 }
    const tex = TitleHotpoint.canvasTexture(title, size)
    const material = new THREE.SpriteMaterial({ map: tex, transparent: true })
    material.sizeAttenuation = false

    const obj = new THREE.Sprite(material)
    obj.center.set(1, 0)
    obj.scale.set(size.x / 64 * 0.05, 1 * 0.05, 1)
    this.defaultScale = obj.scale.clone()
    this.group.add(obj)

    this.interactable = false
  }

  resize(ratio) {
    this.group.children[0].scale.copy(this.defaultScale).multiplyScalar(ratio)
  }

  /**
   * 创建canvas贴图，用于在场景显示贴图
   * @param {string} title 传入需要在页面显示的字
   * @param {Object} size 创建的热点的长宽
   */
  static canvasTexture(title, size) {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const font = 'bold 24px ' + (detectIOS() ? 'PingFang SC' : 'Microsoft YaHei')
    ctx.font = font
    const width = ctx.measureText(title).width
    canvas.width = 25 + width
    canvas.height = 64

    ctx.save()
    ctx.font = font
    ctx.fillStyle = 'white'
    ctx.textBaseLine = 'middle'
    ctx.textAlign = 'left'
    ctx.fillText(title, 0, 30)
    ctx.restore()

    ctx.save()
    ctx.lineWidth = 3
    ctx.strokeStyle = 'rgba(0, 0, 0, 255)'

    ctx.beginPath()
    ctx.moveTo(width / 3, 42)
    ctx.lineTo(width - 2, 42)

    ctx.moveTo(0, 37)
    ctx.lineTo(width, 37)
    ctx.lineTo(width + 20, 56)
    ctx.stroke()

    ctx.lineWidth = 2
    ctx.arc(width + 20, 56, 2, 0, 2 * Math.PI)
    ctx.stroke()
    ctx.restore()

    size.x = canvas.width
    size.y = canvas.height

    const canvasTexture = new THREE.CanvasTexture(canvas)
    canvasTexture.wrapS = THREE.ClampToEdgeWrapping
    canvasTexture.wrapT = THREE.ClampToEdgeWrapping
    canvasTexture.needsUpdate = true
    return canvasTexture
  }
}

/**
 * 弹窗类型热点
 */
export class PopWindowsHotpoint extends InteractableHotpoint {
  /**
   * 热点初始化函数
   * @param {string} id 热点id
   * @param {string} title 热点标题
   * @param {THREE.Vector3} pos 热点坐标
   * @param {string} windowType 热点类型
   * @param {number} cpointId 热点对应相机点位id
   * @param {string} tagUrl 热点图标的url
   * @param {Object} style 样式
   * @param {Object} msg 信息
   * @param {AudioManager} audioMgr 音频播放控制器
   */
  constructor(id, title, pos, windowType, cpointId, tagUrl, style, msg, audioMgr) {
    super(id, title, pos, 'Windows', cpointId, tagUrl, style)
    // todo:添加弹窗组件
    this.windowType = windowType
    this.popItem = []
    this.popMsg = []

    switch (windowType) {
      case 'Image':
        this.addImage(msg)
        this.addLink(msg)
        break
      case 'ImageText':
        this.addImage(msg)
        this.addText(msg)
        this.addLink(msg)
        this.addVoice(msg)
        this.audioMgr = audioMgr
        break
      case 'ImageTextAudio':
        this.addImage(msg)
        this.addText(msg)
        this.addLink(msg)
        this.addAudio(msg)
        this.audioMgr = audioMgr
        break
      case 'Text':
        this.addText(msg)
        this.addVoice(msg)
        this.audioMgr = audioMgr
        break
      case 'Video':
        this.addVideo(msg)
        this.addLink(msg)
        this.audioMgr = audioMgr
        break
      case 'VideoText':
        this.addVideo(msg)
        this.addText(msg)
        this.addLink(msg)
        this.audioMgr = audioMgr
        break
      case 'Link':
        this.addIframe(msg)
        break
      case 'Iframe':
        // todo iframe内部弹窗
        break
    }
  }

  addVoice(msg) {
    this.voice = msg.hotpointVoiceUrl
  }

  addImage(msg) {
    this.popItem.push('image')
    this.popMsg.push(Array.isArray(msg.hotpointImageUrls) ? msg.hotpointImageUrls : Util.strToStrArray(msg.hotpointImageUrls))
  }

  addText(msg) {
    this.popItem.push('text')
    this.popMsg.push({
      title: msg.textTitle,
      text: msg.textBody
    })
  }

  addAudio(msg) {
    this.popItem.push('audio')
    this.popMsg.push(msg.hotpointMediaUrl)
  }

  addVideo(msg) {
    this.popItem.push('video')
    this.popMsg.push(msg.hotpointMediaUrl)
  }

  addLink(msg) {
    this.popItem.push('link')
    this.popMsg.push(msg.link)
  }

  addIframe(msg) {
    this.popItem.push('iframe')
    this.popMsg.push(msg.link)
  }

  onClick() {
    if (this.audioMgr) this.audioMgr.playHp(null, this.voice)
    Util.WindowsUtls.pop(this.popItem, this.popMsg)
  }

  onClose() {
    Util.WindowsUtls.close()
  }

  async getPlayTime() {
    const ml = await Util.WindowsUtls.getMediaLen() * 1000
    return this.palyTime + ml
  }
}

/**
 * 点位跳转标签
 */
export class JumpHotpoint extends InteractableHotpoint {
  /**
   * 热点初始化函数
   * @param {string} id 热点id
   * @param {string} title 热点标题
   * @param {THREE.Vector3} pos 热点坐标
   * @param {number} cpointId 热点对应相机点位id
   * @param {string} tagUrl 热点图标的url
   * @param {string} link 目标转跳链接
   * @param {Object} style 样式
   */
  constructor(id, title, pos, cpointId, tagUrl, link, style) {
    super(id, title, pos, 'Jump', cpointId, tagUrl, style)
    this.link = link
  }

  /**
   * 按键点击事件
   */
  onClick() {
    if (this.link) {
      window.open(this.link, '_blank')
    }
  }
}

/**
 * 场景跳转热点
 */
export class JumpViewHotpoint extends InteractableHotpoint {
  /**
   * 热点初始化函数
   * @param {string} id 热点id
   * @param {string} title 热点标题
   * @param {THREE.Vector3} pos 热点坐标
   * @param {number} cpointId 热点对应相机点位id
   * @param {string} tagUrl 热点图标的url
   * @param {string} targetView 目标跳转场景
   * @param {Object} style 样式
   * @param {ViewSet} vs 样式
   */
  constructor(id, title, pos, cpointId, tagUrl, targetView, style, vs) {
    super(id, title, pos, 'Jump', cpointId, tagUrl, style)
    this.targetView = targetView
    this.vs = vs
  }

  /**
   * 按键事件
   */
  onClick() {
    this.vs.toSceneView(this.targetView)
  }
}

/**
 * 音频标签
 */
export class MusicHotpoint extends InteractableHotpoint {
  /**
   * 热点初始化函数
   * @param {string} id 热点id
   * @param {string} title 热点标题
   * @param {THREE.Vector3} pos 热点坐标
   * @param {number} cpointId 热点对应相机点位id
   * @param {string} tagUrl 热点图标的url
   * @param {string} musicUrl 音频Url
   * @param {Object} style 样式
   * @param {AudioManager} audioMgr 音频管理器
   */
  constructor(id, title, pos, cpointId, tagUrl, musicUrl, style, audioMgr) {
    super(id, title, pos, 'Music', cpointId, tagUrl, style)
    this.url = musicUrl
    this.audioMgr = audioMgr

    this.isplay = false
  }

  /**
   * 点击事件
   */
  onClick() {
    if (this.isplay) {
      this.audioMgr.stopHp()
    } else {
      this.audioMgr.playHp(this.url, null)
    }
    this.isplay = !this.isplay
  }
}

/**
 * 音频标签
 */
export class TopHotpoint extends InteractableHotpoint {
  /**
   * 热点初始化函数
   * @param {string} id 热点id
   * @param {string} title 热点标题
   * @param {THREE.Vector3} pos 热点坐标
   * @param {number} cpointId 热点对应相机点位id
   * @param {string} tagUrl 热点图标的url
   * @param {string} url 音频Url
   * @param {Object} style 样式
   */
  constructor(id, title, pos, cpointId, tagUrl, url, style, events) {
    super(id, title, pos, 'Music', cpointId, tagUrl, style)
    this.url = url
    this.events = events
  }

  /**
   * 点击事件
   */
  onClick() {
    parent.postMessage({ type: Events.ClickTag, msg: { url: this.url, id: this.id, name: this.title } }, '*')
  }
}

/**
 * 热点集合
 */
export class HotpointSet {
  /**
   *
   * @param {ExtraObjectSet} exObjs
   */
  constructor(exObjs) {
    /**
     * @type {ExtraObjectSet} 热点根路径
     */
    this.exObjs = exObjs

    /**
     * @type {THREE.Group} 热点根路径
     */
    this.root = new THREE.Group()

    /**
     * @type {number} 可视半径
     */
    this.visibleRadius = 5000 // 5米
    /**
     * @type {THREE.Object3D[]} 可交互元素
     */
    this.interactObj = []
    /**
     * @type {THREE.Intersection[]} 交互检测结果
     */
    this.interscetResult = []

    /**
     * @type {Map<string, Hotpoint>} 所有热点的id
     */
    this.id2Hotpoint = {}

    /**
     * @type {boolean} 是否在上帝视角显示
     */
    this.showOn3D = false
    /**
     * @type {boolean} 5米检测
     */
    this.fiveMeterTest = true

    this.useable = true

    this.events = new EventEmitter()

    this._visiable = false
  }

  /**
   * 初始化设置
   * @param {boolean} showOn3D 是否在3d视图显示
   * @param {boolean} fiveMeterTest 是否5米检测
   * @memberof HotpointSet
   */
  init(showOn3D, fiveMeterTest) {
    this.showOn3D = showOn3D
    this.fiveMeterTest = !fiveMeterTest
  }

  /**
   * 添加热点
   * @param {Hotpoint} hp 被添加的热点
   */
  addHotpoint(hp) {
    this.id2Hotpoint[hp.id] = hp

    if (hp.interactable) {
      this.interactObj.push(hp.group)
    }
    this.root.add(hp.group)
  }

  /**
   * 移除热点
   * @param {Hotpoint|string} hp 被移除的热点或者热点id
   */
  removeHotpoint(hp) {
    const id = hp
    if (typeof (hp) === typeof ('s') || typeof (hp) === typeof (1)) {
      hp = this.id2Hotpoint[hp]
    }

    // 删除物体
    if (!hp) {
      const exobj = this.exObjs.getObject(id)
      if (exobj) {
        this.exObjs.removeObject(exobj)
      }
      return
    }

    delete this.id2Hotpoint[hp.id]
    this.root.remove(hp.group)

    // 移除交互
    if (hp.interactable) {
      for (let i = 0; i < this.interactObj.length; i++) {
        if (this.interactObj[i] === hp.group) {
          this.interactObj.splice(i, 1)
          break
        }
      }
    }
  }

  /**
   * 计算并设置是否可视
   * @param {THREE.Vector3} cpointPosition 点位坐标
   */
  calAndSetHotPointVisible(cpointPosition) {
    if (!this.visible) return
    const minDistSqu = this.visibleRadius * this.visibleRadius
    this.interactObj.length = 0

    for (const hpKey in this.id2Hotpoint) {
      const hp = this.id2Hotpoint[hpKey]

      if (!this.fiveMeterTest || cpointPosition.distanceToSquared(hp.position) <= minDistSqu) {
        hp.visible = true
        if (hp.interactable) {
          this.interactObj.push(hp.group)
        }
      } else {
        hp.visible = false
      }
    }
  }

  /**
   * 获取id对应的热点
   * @param {string} id 热点id
   * @returns {Hotpoint} 获得的热点
   * @memberof HotpointSet
   */
  getHotpoint(id) {
    let rls = this.id2Hotpoint[id]
    if (!rls) {
      rls = this.exObjs.getObject(id)
    }
    return rls
  }

  /**
   * 鼠标交互检测
   * @param {THREE.Raycaster} raycaster 鼠标射线
   * @returns {Hotpoint} 碰撞到的热点
   */
  interact(raycaster) {
    if (this.root.visible) {
      this.interscetResult.length = 0
      raycaster.intersectObjects(this.interactObj, true, this.interscetResult)
      if (this.interscetResult.length > 0) {
        return this.id2Hotpoint[this.interscetResult[0].object.hpId]
      }
      return null
    } else {
      return null
    }
  }

  update() {
    for (const hpKey in this.id2Hotpoint) {
      if (this.id2Hotpoint[hpKey].visible && this.id2Hotpoint[hpKey].update) {
        this.id2Hotpoint[hpKey].update()
      }
    }
  }

  resize(ratio) {
    for (const hpKey in this.id2Hotpoint) {
      this.id2Hotpoint[hpKey].resize(ratio)
    }
  }

  get visible() {
    return this.root.visible
  }

  set visible(value) {
    if (this.showOn3D) {
      this.root.visible = true
      return
    }
    this.root.visible = value
  }

  set enable(value) {
    this.useable = value
    this.root.visible = value && this.root.visible
  }

  get enable() {
    return this.useable
  }
}

/**
 * 初始化热点
 * @param {HotpointSet} hps 模型配置
 * @param {ExtraObjectSet} exobjs 模型配置
 * @param {boolean} isShowIn3d 模型配置
 * @param {boolean} isFiveMeter 模型配置
 * @param {object} hpMsg 模型配置
 * @param {AudioManager} audioMgr 模型配置
 * @param {ViewSet} viewSet 模型配置
 */
export function initHotpoints(hps, exobjs, isShowIn3d, isFiveMeter, hpMsg, audioMgr, viewSet) {
  hps.init(isShowIn3d, isFiveMeter)
  // 添加热点
  for (const msg of hpMsg) {
    const pos = Util.strToNumberArray(msg.hotpointPosition)
    const normal = msg.normal && Util.strToNumberArray(msg.normal)
    const corner = msg.normal && Util.strToNumberArray(msg.cornerPosition)

    const style = !msg.fontSize ? undefined : {
      isShowTitle: msg.isShowTitle,
      fontSize: msg.fontSize,
      switchAttenuation: msg.switchAttenuation,
      fontColor: msg.fontColor,
      backgroundColor: msg.backgroundColor
    }
    addHotpoint(hps, exobjs, audioMgr, viewSet, msg, style, pos, normal, corner)
  }
}

/**
 * 初始化热点
 * @param {HotpointSet} hps 热点集合
 * @param {ExtraObjectSet} exobjs 额外的obj
 * @param {AudioManager} audioMgr 音频管理
 * @param {ViewSet} viewSet 场景集合
 * @param {object} msg 热点信息，见后端表
 * @param {object} style 热点标题样式
 * @param {number[]} posArray 位置坐标数组
 * @param {number[]} normal 方向向量数组
 * @param {number[]} corner 角落坐标数组
 */
export function addHotpoint(hps, exobjs, audioMgr, viewSet, msg, style, posArray, normal, corner, playImmediately = false) {
  const pos = new THREE.Vector3().fromArray(posArray)
  switch (msg.hotpointType) {
    case 'Title':
      hps.addHotpoint(
        new TitleHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos, msg.pointId
        ))
      break
    case 'Image':
      hps.addHotpoint(
        new PopWindowsHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos,
          'Image', msg.pointId, msg.hotpointIconUrl,
          style, msg
        ))
      break
    case 'ImageText':
      hps.addHotpoint(
        new PopWindowsHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos,
          'ImageText', msg.pointId, msg.hotpointIconUrl,
          style, msg, audioMgr
        ))
      break
    case 'ImageTextAudio':
      hps.addHotpoint(
        new PopWindowsHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos,
          'ImageTextAudio', msg.pointId, msg.hotpointIconUrl,
          style, msg, audioMgr
        ))
      break
    case 'Text':
      hps.addHotpoint(
        new PopWindowsHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos,
          'Text', msg.pointId, msg.hotpointIconUrl,
          style, msg, audioMgr
        ))
      break
    case 'Audio':
      hps.addHotpoint(
        new MusicHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos, msg.pointId,
          msg.hotpointIconUrl, msg.hotpointMediaUrl, style, audioMgr
        ))
      break
    case 'Video':
      hps.addHotpoint(
        new PopWindowsHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos,
          'Video', msg.pointId, msg.hotpointIconUrl,
          style, msg, audioMgr
        ))
      break
    case 'VideoText':
      hps.addHotpoint(
        new PopWindowsHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos,
          'VideoText', msg.pointId, msg.hotpointIconUrl,
          style, msg, audioMgr
        ))
      break
    case 'Jump':
      hps.addHotpoint(
        new JumpHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos,
          msg.pointId, msg.hotpointIconUrl, msg.link, style
        ))
      break
    case 'Link':
      hps.addHotpoint(
        new PopWindowsHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos,
          'Link', msg.pointId, msg.hotpointIconUrl,
          style, msg
        ))
      break
    case 'Iframe':
      hps.addHotpoint(
        new PopWindowsHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos,
          'Iframe', msg.pointId, msg.hotpointIconUrl,
          style, msg
        ))
      break
    case 'Top':
      hps.addHotpoint(
        new TopHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos, msg.pointId,
          msg.hotpointIconUrl, msg.link, style, hps.events
        ))
      break
    case 'JumpView':
      hps.addHotpoint(
        new JumpViewHotpoint(
          msg.hotpointId, msg.hotpointTitle, pos,
          msg.pointId, msg.hotpointIconUrl, msg.jumpId, style, viewSet
        ))
      break
    case 'VideoPlane':
      exobjs.addObject(
        new VerticalVedioPanel(
          msg.hotpointId, msg.hotpointTitle,
          pos, new THREE.Vector3().fromArray(corner),
          new THREE.Vector3().fromArray(normal), msg.hotpointMediaUrl,
          playImmediately
        ))
      break
  }
}
